import React, { Fragment, useEffect, useState } from "react";
import { Row, Table, Button, Col } from "reactstrap";
import { Link } from "react-router-dom";

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender
} from '@tanstack/react-table';

import { rankItem } from '@tanstack/match-sorter-utils';
import JobListGlobalFilter from "./GlobalSearchFilter";
import { matchesSearch } from "../../helpers/search";

// --- GLOBAL ARAMA ---
// Eskiden yalnızca kolonun ham değerinde (çoğu zaman ID) fuzzy arıyordu: ekranda
// görünen kullanıcı/kategori adı gibi $expand ile gelen alanlar hiç aranmıyor,
// "Traktör" yazınca KATEGORI_ID=3135 olan satır bulunmuyordu. Artık satırın
// tüm alanları (iç içe nesneler dahil) Türkçe duyarsız "içerir" ile aranıyor.
// Birden çok kelime yazılırsa hepsi geçmeli (ör. "ahmet traktör").
const rowGlobalFilter = (row, columnId, filterValue) =>
  matchesSearch(row.original, filterValue);

// Column Filter
const Filter = ({ column }) => {
  const columnFilterValue = column.getFilterValue();

  return (
    <>
      <DebouncedInput
        type="text"
        value={(columnFilterValue ?? '')}
        onChange={value => column.setFilterValue(value)}
        placeholder="Arama..."
        className="w-36 border shadow rounded"
        list={column.id + 'list'}
      />
      <div className="h-1" />
    </>
  );
};

// Global Filter
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <React.Fragment>
      <Col sm={4}>
        <input {...props} value={value} onChange={e => setValue(e.target.value)} />
      </Col>
    </React.Fragment>
  );
};

const TableContainer = ({
  columns,
  data,
  tableClass,
  theadClass,
  divClassName,
  isBordered,
  isPagination,
  isGlobalFilter,
  paginationWrapper,
  SearchPlaceholder,
  pagination,
  buttonClass,
  buttonName,
  isAddButton,
  isCustomPageSize,
  handleUserClick,
  isJobListGlobalFilter,
  // Sayfa kendi sıralamasını yapıyorsa: değer değişince 1. sayfaya dön
  // (yoksa 5. sayfadayken sıralama değişince ortadan bir dilim görünür).
  resetPageKey,
}) => {

  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const fuzzyFilter = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({ itemRank });
    return itemRank.passed;
  };

  const table = useReactTable({
    columns,
    data,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: rowGlobalFilter,
    // Varsayılan kural yalnızca ilk satırı string/number olan kolonlarda arar;
    // tüm kolonları display-only olan tablolarda arama hiç çalışmıyordu.
    getColumnCanGlobalFilter: () => true,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    autoResetPageIndex: false, 
  });

  useEffect(() => {
    if (resetPageKey !== undefined) table.setPageIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetPageKey]);

  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
    getPageOptions,
    setPageIndex,
    nextPage,
    previousPage,
    getState
  } = table;

  // --- AKILLI SAYFALANDIRMA (Smart Pagination) MANTIĞI ---
  // Eğer 50 sayfa varsa hepsini ekrana basmak yerine aktif sayfanın etrafındaki max 5 sayfayı gösterir.
  const currentPageIndex = getState().pagination.pageIndex;
  const pageOptions = getPageOptions();
  const totalPages = pageOptions.length;
  
  let startPage = Math.max(0, currentPageIndex - 2);
  let endPage = Math.min(totalPages - 1, currentPageIndex + 2);

  if (endPage - startPage < 4) {
    if (startPage === 0) {
      endPage = Math.min(totalPages - 1, 4);
    } else if (endPage === totalPages - 1) {
      startPage = Math.max(0, totalPages - 5);
    }
  }
  const visiblePages = pageOptions.slice(startPage, endPage + 1);

  // Gösterilen kayıt aralığını hesaplama (Örn: 1 - 10)
  const pageIndex = getState().pagination.pageIndex;
  const pageSize = getState().pagination.pageSize;
  const firstRowIndex = pageIndex * pageSize + 1;
  const lastRowIndex = Math.min((pageIndex + 1) * pageSize, data.length);

  return (
    <Fragment>
      <Row className="mb-3 align-items-center">
        {isCustomPageSize && (
          <Col sm={3} md={2} className="mb-2 mb-sm-0">
            <div className="d-flex align-items-center">
              <select
                className="form-select form-select-sm"
                value={pageSize}
                onChange={e => table.setPageSize(Number(e.target.value))}
              >
                {[10, 20, 30, 40, 50].map(size => (
                  <option key={size} value={size}>
                    {size} Göster
                  </option>
                ))}
              </select>
            </div>
          </Col>
        )}

        {isGlobalFilter && (
          <Col sm={6} md={4} className="mb-2 mb-sm-0">
             <DebouncedInput
                value={globalFilter ?? ''}
                onChange={value => setGlobalFilter(String(value))}
                className="form-control form-control-sm search-box"
                placeholder={SearchPlaceholder || "Ara..."}
             />
          </Col>
        )}

        {isJobListGlobalFilter && <JobListGlobalFilter setGlobalFilter={setGlobalFilter} />}

        {isAddButton && (
          <Col sm={12} md={isGlobalFilter || isCustomPageSize ? 6 : 12}>
            <div className="text-sm-end">
              <Button type="button" className={buttonClass} onClick={handleUserClick}>
                <i className="mdi mdi-plus me-1"></i> {buttonName}
              </Button>
            </div>
          </Col>
        )}
      </Row>

      <div className={divClassName ? divClassName : "table-responsive"}>
        <Table hover className={tableClass} bordered={isBordered}>
          <thead className={theadClass || "table-light"}>
            {getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <th key={header.id} colSpan={header.colSpan} className={`${header.column.columnDef.enableSorting ? "sorting" : ""}`}>
                      {header.isPlaceholder ? null : (
                        <React.Fragment>
                          <div
                            {...{
                              className: header.column.getCanSort() ? 'cursor-pointer select-none d-flex align-items-center' : 'd-flex align-items-center',
                              onClick: header.column.getToggleSortingHandler(),
                            }}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            <span className="ms-1">
                              {{ asc: ' 🔼', desc: ' 🔽' }[header.column.getIsSorted()] ?? null}
                            </span>
                          </div>
                          {header.column.getCanFilter() ? (
                            <div className="mt-2">
                              <Filter column={header.column} table={table} />
                            </div>
                          ) : null}
                        </React.Fragment>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {getRowModel().rows.length > 0 ? (
                getRowModel().rows.map(row => {
                  return (
                    <tr key={row.id}>
                      {row.getVisibleCells().map(cell => {
                        return (
                          <td key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
            ) : (
                <tr>
                    <td colSpan={columns.length} className="text-center p-4 text-muted">
                        Kayıt bulunamadı.
                    </td>
                </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* --- YENİ MODERN PAGINATION (SAYFALANDIRMA) --- */}
      {isPagination && data.length > 0 && (
        <Row className="align-items-center mt-3">
          <Col sm={12} md={5}>
            <div className="text-muted font-size-13 mb-2 mb-md-0">
              <span className="fw-semibold">{data.length}</span> kayıttan <span className="fw-semibold">{firstRowIndex}</span> ile <span className="fw-semibold">{lastRowIndex}</span> arası gösteriliyor.
            </div>
          </Col>
          <Col sm={12} md={7}>
            <div className={paginationWrapper || "d-flex justify-content-md-end"}>
              <ul className={pagination || "pagination pagination-rounded mb-0"}>
                
                {/* Önceki Butonu */}
                <li className={`page-item ${!getCanPreviousPage() ? "disabled" : ""}`}>
                  <Link to="#" className="page-link" onClick={(e) => { e.preventDefault(); previousPage(); }}>
                    <i className="mdi mdi-chevron-left"></i>
                  </Link>
                </li>

                {/* Sadece aktif sayfanın etrafındaki (visiblePages) butonları bas */}
                {startPage > 0 && (
                   <li className="page-item disabled"><span className="page-link">...</span></li>
                )}

                {visiblePages.map((item, key) => (
                  <li key={key} className={`page-item ${currentPageIndex === item ? "active" : ""}`}>
                    <Link to="#" className="page-link" onClick={(e) => { e.preventDefault(); setPageIndex(item); }}>
                      {item + 1}
                    </Link>
                  </li>
                ))}

                {endPage < totalPages - 1 && (
                   <li className="page-item disabled"><span className="page-link">...</span></li>
                )}

                {/* Sonraki Butonu */}
                <li className={`page-item ${!getCanNextPage() ? "disabled" : ""}`}>
                  <Link to="#" className="page-link" onClick={(e) => { e.preventDefault(); nextPage(); }}>
                    <i className="mdi mdi-chevron-right"></i>
                  </Link>
                </li>

              </ul>
            </div>
          </Col>
        </Row>
      )}
    </Fragment>
  );
};

export default TableContainer;